import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/features/notifications/api';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: ['notifications', 'list'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
};

export function useNotifications(enabled: () => boolean) {
  return useQuery({
    queryKey: notificationKeys.list,
    queryFn: ({ signal }) => fetchNotifications(1, signal),
    enabled,
  });
}

/**
 * Polled once a minute. Not a websocket: a count that is at most sixty seconds stale is
 * indistinguishable from a live one for somebody deciding whether to open a panel, and a
 * persistent connection is a subsystem with its own failure modes.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: ({ signal }) => fetchUnreadCount(signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: () => client.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => client.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
